import React from "react";
import { Stack, Button, Switch, Typography, Box, IconButton, Menu, MenuItem, useTheme, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import { LegendItem } from "../LegendItem";
import { FormattedMessage, useIntl } from "react-intl";

interface ExportMenuProps {
    roleTenure: string | undefined;
    sharedHistory: boolean | undefined;
    hasSharedHistories: boolean;
    anchorEl: HTMLElement | null;
    open: boolean;
    showTransactions: boolean | undefined;
    handleToggleCurrentPrior: (roleType: string) => void;
    handleToggleTransactionLines: (shouldShowLines: boolean) => void;
    handleToggleSharedHistory: (checked: boolean) => void;
    handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleClose: (type?: "image/png" | "image/svg+xml" | undefined) => void;
}

const ExportMenu = ({
    roleTenure,
    sharedHistory,
    hasSharedHistories,
    anchorEl,
    open,
    showTransactions,
    handleToggleCurrentPrior,
    handleToggleTransactionLines,
    handleToggleSharedHistory,
    handleClick,
    handleClose,
}: ExportMenuProps) => {
    const intl = useIntl();
    const theme = useTheme();

    const RoleButton = ({ color, roleType, label }: { color: string; roleType: string; label: string }) => {
        return (
            <Button
                sx={{
                    padding: 0,
                    minWidth: "auto",
                    height: "auto",
                    mr: 2,
                    opacity: roleTenure === "" || roleTenure === undefined || roleTenure === roleType ? 1 : 0.5,
                }}
                onClick={() => handleToggleCurrentPrior(roleType)}
            >
                <LegendItem color={color} label={label} />
            </Button>
        );
    };

    const SwitchContent = React.forwardRef(function SwitchContent(props, ref) {
        return (
            <Box ref={ref} {...props} sx={{ display: "inline-flex", alignItems: "center" }}>
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, color: !hasSharedHistories ? theme.palette.text.disabled : "" }}
                >
                    <FormattedMessage id="tenureChart.filters.sharedHistories" />
                </Typography>
                <Switch
                    checked={sharedHistory}
                    size="small"
                    disabled={!hasSharedHistories}
                    onChange={(event, checked) => handleToggleSharedHistory(checked)}
                />
            </Box>
        );
    });

    return (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={2}>
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                <RoleButton
                    color={theme.palette.secondary.light}
                    roleType="target_previous"
                    label={intl.formatMessage({ id: "tenureChart.options.priorRoleTenure" })}
                />
                <RoleButton
                    color={theme.palette.others.darkestColor}
                    roleType="target_current"
                    label={intl.formatMessage({ id: "tenureChart.options.currentRoleTenure" })}
                />
                <Button
                    sx={{
                        padding: 0,
                        minWidth: "auto",
                        height: "auto",
                        opacity: showTransactions ? 1 : 0.5,
                    }}
                    onClick={() => handleToggleTransactionLines(!showTransactions)}
                >
                    <LegendItem color={theme.palette.others.red} label="Transactions" type="line" />
                </Button>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
                {!hasSharedHistories ? (
                    <Tooltip
                        arrow
                        placement="bottom-end"
                        title={intl.formatMessage({ id: "tenureChart.sharedHistories.tooltip" })}
                    >
                        <SwitchContent />
                    </Tooltip>
                ) : (
                    <SwitchContent />
                )}

                <Box>
                    <IconButton size="small" onClick={handleClick}>
                        <MenuIcon />
                    </IconButton>
                    <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={() => handleClose()}>
                        <MenuItem sx={{ fontSize: 11 }} onClick={() => handleClose("image/png")}>
                            <FormattedMessage id="tenureChart.exportMenu.downloadpng" />
                        </MenuItem>
                        <MenuItem sx={{ fontSize: 11 }} onClick={() => handleClose("image/svg+xml")}>
                            <FormattedMessage id="tenureChart.exportMenu.downloadsvg" />
                        </MenuItem>
                    </Menu>
                </Box>
            </Stack>
        </Stack>
    );
};

export default ExportMenu;
